import { QueryHookOptions, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { UPDATE_NOTIFICATIONS_QUERY } from 'src/graphql/entities/notification/notification.operations';
import { Notification } from 'src/graphql/entities/notification/notification.types';
import {
  getBaseNotificationFilters,
  getOptimisticItemsByFilters,
} from 'src/graphql/entities/notification/utils';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { ConditionInput } from 'src/graphql/types';
import { NotificationRaw } from 'src/types';

export interface UpdateNotification {
  updateNotification: Omit<
    NotificationRaw,
    | 'flowInitiatorEntity'
    | 'platformUserFlowInitiator'
    | 'clientFlowInitiator'
    | 'partner'
    | 'platformUser'
  >[];
}

export const useUpdateNotification = () => {
  const { user } = useCurrentUserProfile();

  const [updateNotification, { loading: isLoading, client }] = useMutation<UpdateNotification>(
    UPDATE_NOTIFICATIONS_QUERY,
  );

  const updateSpecificNotifications = useCallback(
    (data: {
      updateItems: Notification[];
      patch: Pick<Notification, 'status'>;
      onCompleted?: QueryHookOptions['onCompleted'];
    }) => {
      if (!user?.id) {
        return;
      }

      const { updateItems, patch, onCompleted } = data;

      void updateNotification({
        variables: {
          data: patch,
          conditions: [
            ...getBaseNotificationFilters(user.id),
            {
              attribute: 'notificationId',
              operator: 'in',
              value: updateItems.map((i) => i.id).join(','),
            },
          ],
        },
        optimisticResponse: {
          updateNotification: updateItems.map((i) => ({
            __typename: 'Notification',
            ...i,
            ...patch,
          })),
        },
        onCompleted,
      });
    },
    [updateNotification, user?.id],
  );

  const updateNotificationsByCondition = useCallback(
    (data: {
      conditions: ConditionInput[];
      patch: Pick<Notification, 'status'>;
      onCompleted?: QueryHookOptions['onCompleted'];
    }) => {
      if (!user?.id) {
        return;
      }

      const { conditions, patch, onCompleted } = data;
      const allFilters = [...getBaseNotificationFilters(user.id), ...conditions];

      void updateNotification({
        variables: {
          data: patch,
          conditions: allFilters,
        },
        optimisticResponse: {
          updateNotification: getOptimisticItemsByFilters(allFilters, client).map((i) => ({
            __typename: 'Notification',
            ...i,
            ...patch,
          })),
        },
        onCompleted,
      });
    },
    [client, updateNotification, user?.id],
  );

  return {
    updateSpecificNotifications,
    updateNotificationsByCondition,
    isLoading,
  };
};
